import type { Role } from './api';

/**
 * Client-side role → capability map. Single source of truth for navigation
 * filtering and route gating. Mirrors the backend RBAC (#65); the server
 * remains the enforcement point, this layer only shapes the UI.
 */
export type Capability =
  | 'viewOverview'
  | 'manageLicenses'
  | 'viewBlacklist'
  | 'viewAudit'
  | 'manageUsers'
  | 'submitLicenseRequests'
  | 'reviewLicenseRequests';

const ROLE_CAPABILITIES: Record<Role, ReadonlySet<Capability>> = {
  admin: new Set<Capability>([
    'viewOverview',
    'manageLicenses',
    'viewBlacklist',
    'viewAudit',
    'manageUsers',
    'submitLicenseRequests',
    'reviewLicenseRequests',
  ]),
  developer_operator: new Set<Capability>([
    'viewOverview',
    'manageLicenses',
    'viewBlacklist',
    'viewAudit',
    'reviewLicenseRequests',
  ]),
  manufacturer_operator: new Set<Capability>(['submitLicenseRequests']),
};

export function hasCapability(role: Role, capability: Capability): boolean {
  return ROLE_CAPABILITIES[role].has(capability);
}

/** True when the role has at least one of the listed capabilities. */
export function hasAnyCapability(role: Role, capabilities: readonly Capability[]): boolean {
  return capabilities.some((c) => ROLE_CAPABILITIES[role].has(c));
}

/** Default landing page for a role after login / when redirected off a gated route. */
export function homePathForRole(role: Role): string {
  return hasCapability(role, 'viewOverview') ? '/' : '/requests';
}
